import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import {
  AcceptIdealPreview,
  LineItemBudgetChange,
  LineItemBudgetTarget,
  usePreviewIdealMarginAcceptMutation,
} from "../../../store/apis/associatedCampaignMarginApi";
import { fmtMoneyDetailed, fmtPct } from "../../../utils/format";
import { EnrichedRow, getErrorMessage, marginFromText } from "./derive";

export interface AcceptIdealRequest {
  /** Fila tal cual viene del servidor. */
  row: EnrichedRow;
  /** Margen a guardar (el ideal o el editado). */
  margin: number;
  /** Inversión plataforma que deja ese margen. */
  investment: number;
}

interface AcceptIdealDialogProps {
  request: AcceptIdealRequest | null;
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (
    description: string | null,
    lineItemBudgets: LineItemBudgetTarget[],
    /** Line items de YouTube: la API no deja cambiarlos, su presupuesto se pone a mano */
    manualLineItems: LineItemBudgetChange[]
  ) => void;
}

const fmtDay = (iso: string | null | undefined): string =>
  iso ? dayjs(iso.split("T")[0]).format("DD/MM/YYYY") : "—";

/** Importe escrito a mano ("1.234,56" o "1234.56") → número, o null si no lo es. */
export function parseAmount(text: string): number | null {
  const clean = text.trim().replace(/\s|€/g, "");
  const normalized = clean.includes(",")
    ? clean.replace(/\./g, "").replace(",", ".")
    : clean;
  const n = Number(normalized);
  return normalized !== "" && Number.isFinite(n) ? n : null;
}

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: "activo",
  PAUSED: "pausado",
  DRAFT: "borrador",
  ARCHIVED: "archivado",
};

/** Confirma el margen ideal: qué tramo se crea, qué inversión queda y qué cambia en DV360. */
const AcceptIdealDialog: React.FC<AcceptIdealDialogProps> = ({
  request,
  isSaving,
  error,
  onClose,
  onConfirm,
}) => {
  const [description, setDescription] = useState("");
  const [preview, setPreview] = useState<AcceptIdealPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Presupuesto nuevo de cada line item, tal como se escribe (por lineItemId).
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loadPreview, { isLoading: isPreviewing }] =
    usePreviewIdealMarginAcceptMutation();

  useEffect(() => {
    setDescription("");
    setPreview(null);
    setPreviewError(null);
    setDrafts({});
    if (!request?.row.dv360AutoApply) return;
    let cancelled = false;
    loadPreview({
      params: {
        briefingId: request.row.briefingId,
        associatedId: request.row.associatedCampaignId,
      },
      body: { margin: request.margin },
    })
      .unwrap()
      .then((res) => {
        if (cancelled || !res.data) return;
        setPreview(res.data);
        setDrafts(
          Object.fromEntries(
            res.data.lineItemChanges.map((c) => [
              c.lineItemId,
              c.budgetAfter.toFixed(2),
            ])
          )
        );
      })
      .catch((err) => {
        if (!cancelled)
          setPreviewError(
            getErrorMessage(err) || "No se ha podido calcular el cambio"
          );
      });
    return () => {
      cancelled = true;
    };
  }, [request, loadPreview]);

  if (!request) return null;
  const { row, margin, investment } = request;
  const fromDate = row.marginForecast?.fromDate ?? null;
  const closesOn = fromDate
    ? dayjs(fromDate).subtract(1, "day").format("YYYY-MM-DD")
    : null;

  const changes = preview?.lineItemChanges ?? [];
  const manualChanges = preview?.manualLineItemChanges ?? [];
  const lineItemRows = changes.map((c) => {
    const value = parseAmount(drafts[c.lineItemId] ?? "");
    const invalid =
      value === null
        ? "Escribe un importe"
        : value < c.spent - 0.01
          ? `No puede bajar de lo gastado (${fmtMoneyDetailed(c.spent, 2)})`
          : null;
    return { change: c, value, invalid };
  });
  const hasInvalid = lineItemRows.some((r) => r.invalid !== null);
  const ioIncrease =
    preview?.ioBudgetBefore != null && preview.ioBudgetAfter != null
      ? preview.ioBudgetAfter - preview.ioBudgetBefore
      : null;
  const lineItemIncrease = lineItemRows.reduce(
    (acc, r) =>
      acc + ((r.value ?? r.change.budgetBefore) - r.change.budgetBefore),
    0
  );

  const rows = [
    {
      label: `Margen % (${marginFromText(row)})`,
      before: fmtPct(row.marginForecast?.currentMargin ?? NaN),
      after: fmtPct(margin),
    },
    {
      label: "Inversión plataforma",
      before: fmtMoneyDetailed(row.platformInversion, 2),
      after: fmtMoneyDetailed(investment, 2),
    },
  ];

  const confirm = () =>
    onConfirm(
      description.trim() === "" ? null : description.trim(),
      lineItemRows
        .filter((r) => r.value !== null)
        .map((r) => ({
          lineItemId: r.change.lineItemId,
          maxAmount: Math.round((r.value as number) * 100) / 100,
        })),
      manualChanges
    );

  return (
    <Dialog
      open
      onClose={isSaving ? undefined : onClose}
      maxWidth={changes.length > 0 ? "md" : "sm"}
      fullWidth
    >
      <DialogTitle>Aceptar margen ideal</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2">
            <strong>{row.associatedCampaignName}</strong> pasará a un margen del{" "}
            <strong>{fmtPct(margin)}</strong> del {fmtDay(fromDate)} al{" "}
            {fmtDay(row.endDate)}. El tramo que está en vigor se cierra el{" "}
            {fmtDay(closesOn)}; si ya lo habías cambiado hoy, se sustituye.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell align="right">Ahora</TableCell>
                <TableCell align="right">Al aceptar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.label}>
                  <TableCell>{r.label}</TableCell>
                  <TableCell align="right">{r.before}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {r.after}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {row.dv360AutoApply ? (
            <Alert severity="info">
              El presupuesto de la IO {row.sourceId} en DV360 en estas fechas
              pasará
              {preview?.ioBudgetBefore != null
                ? ` de ${fmtMoneyDetailed(preview.ioBudgetBefore, 2)}`
                : ""}{" "}
              a <strong>{fmtMoneyDetailed(investment, 2)}</strong>. Si DV360 lo
              rechaza, no se guarda nada y puedes reintentarlo.
            </Alert>
          ) : (
            <Alert severity="warning">
              Esta línea no se actualiza sola en {row.platform}: se guarda el
              margen, pero la inversión hay que cambiarla a mano en la
              plataforma.
            </Alert>
          )}

          {isPreviewing && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">
                Revisando los line items en DV360…
              </Typography>
            </Box>
          )}
          {previewError && (
            <Alert severity="warning">
              No se ha podido revisar los line items ({previewError}). Si
              aceptas, solo se cambia la IO.
            </Alert>
          )}
          {preview?.dv360Error && (
            <Alert severity="warning">
              No se ha podido leer DV360 ({preview.dv360Error}): si aceptas,
              solo se cambia la IO.
            </Alert>
          )}

          {changes.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="subtitle2">
                Line items con presupuesto fijo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Con presupuesto fijo no siguen a la IO. En DV360 su presupuesto
                es el total del vuelo (incluye lo gastado y, si usa las fechas
                de la IO, todos sus vuelos). Si es el único, pasa a ser el total
                de la IO; si hay varios, la subida
                {ioIncrease != null
                  ? ` de ${fmtMoneyDetailed(ioIncrease, 2)}`
                  : ""}{" "}
                se reparte según el presupuesto de cada uno. Solo los activos.
                Puedes cambiar los importes.
              </Typography>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ "& td, & th": { fontSize: 12 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Line item</TableCell>
                      <TableCell align="right">Gastado</TableCell>
                      <TableCell align="right">Ahora</TableCell>
                      <TableCell align="right">Nuevo presupuesto</TableCell>
                      <TableCell align="right">Cambio</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItemRows.map(({ change, value, invalid }) => (
                      <TableRow key={change.lineItemId}>
                        <TableCell sx={{ maxWidth: 260 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 600,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {change.name}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 11, color: "text.secondary" }}
                          >
                            {change.lineItemId}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {fmtMoneyDetailed(change.spent, 2)}
                        </TableCell>
                        <TableCell align="right">
                          {fmtMoneyDetailed(change.budgetBefore, 2)}
                        </TableCell>
                        <TableCell align="right" sx={{ minWidth: 150 }}>
                          <TextField
                            size="small"
                            value={drafts[change.lineItemId] ?? ""}
                            onChange={(e) =>
                              setDrafts((prev) => ({
                                ...prev,
                                [change.lineItemId]: e.target.value,
                              }))
                            }
                            error={invalid !== null}
                            helperText={invalid ?? undefined}
                            disabled={isSaving}
                            inputProps={{
                              "aria-label": `Nuevo presupuesto de ${change.name}`,
                              style: { textAlign: "right", fontSize: 12 },
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {value === null
                            ? "—"
                            : fmtMoneyDetailed(value - change.budgetBefore, 2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ "& td": { fontWeight: 700 } }}>
                      <TableCell colSpan={4}>
                        Cambio total de los line items
                        {ioIncrease != null
                          ? ` (la IO cambia ${fmtMoneyDetailed(ioIncrease, 2)} en estas fechas)`
                          : ""}
                      </TableCell>
                      <TableCell align="right">
                        {fmtMoneyDetailed(lineItemIncrease, 2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
              {(preview?.skippedFixedLineItems.length ?? 0) > 0 && (
                <Alert severity="warning">
                  No se tocan por no estar activos:{" "}
                  {preview?.skippedFixedLineItems
                    .map(
                      (li) =>
                        `${li.name}${li.status ? ` (${STATUS_LABEL[li.status] ?? li.status})` : ""}`
                    )
                    .join(", ")}
                  .
                </Alert>
              )}
            </Box>
          )}

          {manualChanges.length > 0 && (
            <Alert severity="warning">
              La API de DV360 no deja cambiar los line items de YouTube. Al
              aceptar, pon a mano en DV360 el presupuesto (Budget and pacing)
              de:
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                {manualChanges.map((c) => (
                  <li key={c.lineItemId}>
                    {c.name} ({c.lineItemId}): de{" "}
                    {fmtMoneyDetailed(c.budgetBefore, 2)} a{" "}
                    <strong>{fmtMoneyDetailed(c.budgetAfter, 2)}</strong>
                  </li>
                ))}
              </Box>
            </Alert>
          )}

          <TextField
            label="Motivo (opcional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color={row.dv360AutoApply ? "primary" : "warning"}
          disabled={isSaving || isPreviewing || hasInvalid}
          onClick={confirm}
        >
          {isSaving
            ? "Guardando…"
            : row.dv360AutoApply
              ? changes.length > 0
                ? "Aceptar y enviar IO y line items"
                : "Aceptar y enviar a DV360"
              : "Aceptar (inversión a mano)"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AcceptIdealDialog;
